import React from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { colors, radii } from '../../theme/tokens';
import type { RealGoogleReview } from '../../data/realGoogleReviews';

const getInitials = (name: string) => {
  const parts = name.trim().split(' ');
  return parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase() : name.slice(0, 2).toUpperCase();
};

export const StarRating: React.FC<{ rating: number; size?: number }> = ({ rating, size = 18 }) => (
  <Box role="img" aria-label={`${rating} out of 5 stars`} sx={{ display: 'inline-flex', gap: 0.25 }}>
    {Array.from({ length: 5 }).map((_, i) => (
      <StarRoundedIcon
        key={i}
        aria-hidden
        sx={{ fontSize: size, color: i < rating ? colors.star : 'rgba(255,255,255,0.18)' }}
      />
    ))}
  </Box>
);

const GoogleMark = () => (
  <Box
    component="span"
    aria-hidden
    sx={{
      fontFamily: '"Product Sans", "Inter", sans-serif',
      fontWeight: 700,
      fontSize: '0.8rem',
      letterSpacing: '-0.01em',
      color: colors.textSubtle,
    }}
  >
    Google review
  </Box>
);

type ReviewCardProps = {
  review: RealGoogleReview;
  clamp?: boolean;
};

const ReviewCard: React.FC<ReviewCardProps> = ({ review, clamp = true }) => (
  <Box
    component="figure"
    sx={{
      m: 0,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      p: { xs: 3, md: 3.5 },
      borderRadius: radii.lg,
      backgroundColor: colors.surface,
      border: `1px solid ${colors.border}`,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
      <StarRating rating={review.rating} />
      <GoogleMark />
    </Box>
    <Typography
      component="blockquote"
      sx={{
        m: 0,
        color: colors.text,
        fontSize: '1rem',
        lineHeight: 1.75,
        flex: 1,
        ...(clamp && {
          display: '-webkit-box',
          WebkitLineClamp: 6,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }),
      }}
    >
      “{review.text}”
    </Typography>
    <Box component="figcaption" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 3 }}>
      <Avatar
        src={review.profile_photo_url}
        alt=""
        sx={{ width: 40, height: 40, bgcolor: 'rgba(255,255,255,0.12)', color: colors.white, fontSize: '0.9rem', fontWeight: 600 }}
      >
        {!review.profile_photo_url && getInitials(review.author_name)}
      </Avatar>
      <Box>
        <Typography sx={{ color: colors.text, fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.3 }}>
          {review.author_name}
        </Typography>
        <Typography sx={{ color: colors.textSubtle, fontSize: '0.8rem' }}>via Google Maps</Typography>
      </Box>
    </Box>
  </Box>
);

export default ReviewCard;
